import type { ICitation } from 'src/types/citation';
import type { CitationForType } from 'src/actions/citations';

import { toast } from 'sonner';
import { useState } from 'react';

import { Box, Stack, Button, IconButton, Typography } from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';

import {
  useAddCitation,
  useGetCitations,
  useDeleteCitation,
  useUpdateCitation,
} from 'src/actions/citations';

import { Iconify } from 'src/components/iconify';
import { ConfirmDialog } from 'src/components/custom-dialog';

import { useAuthContext } from 'src/auth/hooks';
import { hasSuperUserOrBetaAccess } from 'src/auth/utils/permissions';

import { ContentFlagContentType } from 'src/types/content-flag';

import { TooltipInfo } from '../tooltip-info';
import { CitationForm } from './citation-form';
import { NewContentFlagDialog } from '../content-flag/new-content-flag-dialog';

// ----------------------------------------------------------------------

const MAX_CITATIONS = 100;

type Props = {
  type: CitationForType;
  guid?: string | null;
  title?: string;
  icon?: JSX.Element;
  minHeight?: string;
  citationIsOnAtlasContent?: boolean;
};

export function CitationsList({
  type,
  guid = null,
  title = 'Sources',
  icon,
  minHeight = '0px',
  citationIsOnAtlasContent = false,
}: Props) {
  const { userProfile } = useAuthContext();
  const canContentFlags = hasSuperUserOrBetaAccess(userProfile);

  const { citations, citationsLoading } = useGetCitations(type, guid);
  const { addCitation } = useAddCitation(type);
  const { deleteCitation } = useDeleteCitation(type, guid || '');
  const { updateCitation } = useUpdateCitation(type, guid || '');
  const addMode = useBoolean();
  const [editingCitation, setEditingCitation] = useState<ICitation | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; guid: string | null }>({
    open: false,
    guid: null,
  });
  const noSourcesText = 'No sources have been added';

  const [newContentFlagDialogOpen, setNewContentFlagDialogOpen] = useState(false);

  const handleAdd = async (data: { details: string; url: string; body?: string }) => {
    if (citations.length >= MAX_CITATIONS) {
      toast.error(`Maximum of ${MAX_CITATIONS} sources reached`);
      return;
    }

    try {
      await addCitation(data, guid || '');
      toast.success('Source added successfully');
      addMode.onFalse();
    } catch (error) {
      console.error(error);
    }
  };

  const handleEdit = async (data: { details: string; url: string; body?: string }) => {
    if (!editingCitation) return;

    try {
      await updateCitation(editingCitation.guid, data);
      toast.success('Source updated successfully');
      if (citationIsOnAtlasContent && canContentFlags) {
        setNewContentFlagDialogOpen(true);
      } else {
        setEditingCitation(null);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.guid) return;

    try {
      await deleteCitation(deleteDialog.guid);
      toast.success('Source deleted successfully');
      setDeleteDialog({ open: false, guid: null });
    } catch (error) {
      console.error(error);
    }
  };

  if (citationsLoading) {
    return null;
  }

  return (
    <>
      <Stack spacing={3} sx={{ minHeight }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            {icon || <Iconify icon="mdi:book-open-variant" width={24} />}
            <Typography variant="h6">{title}</Typography>
            <TooltipInfo title="Add references and sources" />
          </Stack>
          <Button
            variant="contained"
            color="inherit"
            onClick={addMode.onTrue}
            disabled={!guid || citations.length >= MAX_CITATIONS}
          >
            New source
          </Button>
        </Stack>

        {citations.length >= MAX_CITATIONS && (
          <Typography color="error.main" variant="caption">
            Maximum number of sources ({MAX_CITATIONS}) reached
          </Typography>
        )}

        {addMode.value && (
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 3 }}>
            <CitationForm
              onCancel={addMode.onFalse}
              onSubmit={(data) => handleAdd({ ...data, url: data.url || '', body: data.body || '' })}
            />
          </Box>
        )}

        {citations.length > 0 ? (
          <Stack spacing={2}>
            {citations.map((citation) =>
              editingCitation?.guid === citation.guid ? (
                <Box
                  key={citation.guid}
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 3 }}
                >
                  <CitationForm
                    mode="edit"
                    initialValues={citation}
                    onCancel={() => setEditingCitation(null)}
                    onSubmit={(data) => handleEdit({ ...data, url: data.url || '', body: data.body || '' })}
                  />
                </Box>
              ) : (
                <Stack
                  key={citation.guid}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    bgcolor: 'background.neutral',
                  }}
                >
                  <Stack spacing={1} sx={{ flexGrow: 1, overflow: 'hidden' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }} noWrap>
                      {citation.details}
                    </Typography>
                    {/* {citation.body && (
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}>
                        {citation.body}
                      </Typography>
                    )} */}
                    {citation.url && (
                      <Box>
                        <Typography
                          variant="body2"
                          component="a"
                          href={citation.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ color: 'primary.main', display: 'block' }}
                          noWrap
                        >
                          {citation.url}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <IconButton
                      onClick={() => setEditingCitation(citation)}
                      sx={{ height: 32, width: 32 }}
                    >
                      <Iconify icon="mdi:pencil" />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => setDeleteDialog({ open: true, guid: citation.guid })}
                      sx={{ height: 32, width: 32 }}
                    >
                      <Iconify icon="mdi:trash-can-outline" />
                    </IconButton>
                  </Stack>
                </Stack>
              )
            )}
          </Stack>
        ) : !addMode.value ? (
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Typography color="grey.500" variant="h6">
              {noSourcesText}
            </Typography>
          </Box>
        ) : null}
      </Stack>

      <ConfirmDialog
        open={deleteDialog.open}
        title="Delete Source"
        content="Are you sure you want to delete this source?"
        onClose={() => setDeleteDialog({ open: false, guid: null })}
        onConfirm={handleDelete}
      />

      {canContentFlags && (
        <NewContentFlagDialog
          contentType={ContentFlagContentType.CITATION}
          guid={editingCitation?.guid || ''}
          isRespondingToChange
          onClose={() => {
            setNewContentFlagDialogOpen(false);
            setEditingCitation(null);
          }}
          open={newContentFlagDialogOpen}
        />
      )}
    </>
  );
}
